import { useEffect, useState } from 'react'
import {
  defaultAnswers,
  effectiveSchema,
  isMultiSelect,
  pruneAnswers,
  schemaType,
  type JsonSchema,
  type UiSchema,
} from '../lib/discoverySchema'

type Props = {
  schema: JsonSchema
  uiSchema?: UiSchema
  busy?: boolean
  onSubmit: (answers: Record<string, unknown>) => void
}

function FieldLabel({
  title,
  required,
  hint,
}: {
  title: string
  required: boolean
  hint?: string
}) {
  return (
    <span>
      {title}
      {required ? '' : ' · optional'}
      {hint ? <span className="ctl-hint"> · {hint}</span> : null}
    </span>
  )
}

function NestedObjectFields({
  propKey,
  prop,
  value,
  required,
  onChange,
}: {
  propKey: string
  prop: JsonSchema
  value: Record<string, unknown>
  required: boolean
  onChange: (next: Record<string, unknown>) => void
}) {
  const nestedReq = new Set(prop.required ?? [])
  return (
    <fieldset className="preview-fieldset">
      <legend>
        {prop.title ?? propKey}
        {required ? '' : ' · optional'}
      </legend>
      {Object.entries(prop.properties ?? {}).map(([k, child]) => (
        <SchemaField
          key={k}
          name={k}
          prop={child}
          required={nestedReq.has(k)}
          value={value[k]}
          uiWidget={undefined}
          onChange={(v) => onChange({ ...value, [k]: v })}
        />
      ))}
    </fieldset>
  )
}

function SchemaField({
  name,
  prop,
  required,
  value,
  uiWidget,
  onChange,
}: {
  name: string
  prop: JsonSchema
  required: boolean
  value: unknown
  uiWidget?: string
  onChange: (v: unknown) => void
}) {
  const title = prop.title ?? name
  const t = schemaType(prop)

  if (t === 'object') {
    return (
      <NestedObjectFields
        propKey={name}
        prop={prop}
        value={(value as Record<string, unknown>) ?? {}}
        required={required}
        onChange={onChange}
      />
    )
  }

  if (t === 'boolean') {
    return (
      <label className="ctl preview-check">
        <input
          type="checkbox"
          checked={Boolean(value)}
          onChange={(e) => onChange(e.target.checked)}
        />
        <FieldLabel title={title} required={required} />
      </label>
    )
  }

  if (isMultiSelect(prop)) {
    const opts = (prop.items?.enum ?? []) as string[]
    const selected = Array.isArray(value) ? (value as string[]) : []
    return (
      <fieldset className="preview-fieldset preview-multi">
        <legend>
          <FieldLabel title={title} required={required} hint="multi-select" />
        </legend>
        <div className="preview-check-grid">
          {opts.map((opt) => {
            const on = selected.includes(opt)
            return (
              <label key={opt} className="ctl preview-check">
                <input
                  type="checkbox"
                  checked={on}
                  onChange={() => {
                    onChange(
                      on ? selected.filter((x) => x !== opt) : [...selected, opt],
                    )
                  }}
                />
                <span>{opt}</span>
              </label>
            )
          })}
        </div>
      </fieldset>
    )
  }

  if (prop.enum?.length) {
    return (
      <label className="ctl grow">
        <FieldLabel title={title} required={required} />
        <select
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          required={required}
        >
          <option value="" disabled>
            Select…
          </option>
          {prop.enum.map((opt) => (
            <option key={String(opt)} value={String(opt)}>
              {String(opt)}
            </option>
          ))}
        </select>
      </label>
    )
  }

  if (t === 'integer' || t === 'number') {
    return (
      <label className="ctl">
        <FieldLabel title={title} required={required} />
        <input
          type="number"
          step={t === 'integer' ? 1 : 'any'}
          min={prop.minimum}
          max={prop.maximum}
          value={value === undefined || value === null ? '' : String(value)}
          onChange={(e) => {
            const raw = e.target.value
            if (raw === '') {
              onChange(undefined)
              return
            }
            onChange(t === 'integer' ? Number.parseInt(raw, 10) : Number(raw))
          }}
          required={required}
        />
      </label>
    )
  }

  const format = prop.format
  if (uiWidget === 'textarea') {
    return (
      <label className="ctl grow preview-full">
        <FieldLabel title={title} required={required} />
        <textarea
          rows={3}
          value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          maxLength={prop.maxLength}
        />
      </label>
    )
  }

  const inputType =
    format === 'email'
      ? 'email'
      : format === 'uri' || format === 'url'
        ? 'url'
        : format === 'date'
          ? 'date'
          : 'text'

  return (
    <label className="ctl grow">
      <FieldLabel title={title} required={required} />
      <input
        type={inputType}
        value={String(value ?? '')}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        spellCheck={inputType === 'text'}
      />
    </label>
  )
}

/** Renders ctl fields from JSON Schema (+ light if/then / dependencies). */
export function DiscoverySchemaForm({
  schema,
  uiSchema = {},
  busy,
  onSubmit,
}: Props) {
  const [answers, setAnswers] = useState<Record<string, unknown>>(() =>
    defaultAnswers(schema),
  )
  const eff = effectiveSchema(schema, answers)

  useEffect(() => {
    setAnswers(defaultAnswers(schema))
  }, [schema])

  return (
    <form
      className="k-inline-form preview-form stack"
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit(pruneAnswers(answers, eff))
      }}
    >
      {Object.entries(eff.properties).map(([name, prop]) => (
        <SchemaField
          key={name}
          name={name}
          prop={prop}
          required={eff.required.has(name)}
          value={answers[name]}
          uiWidget={uiSchema[name]?.['ui:widget']}
          onChange={(v) => setAnswers((prev) => ({ ...prev, [name]: v }))}
        />
      ))}
      <button type="submit" disabled={busy}>
        {busy ? '…' : 'Submit'}
      </button>
    </form>
  )
}
